import { useCallback, useRef, useState } from 'react';
import { IContactForms } from '../../redux/initState/InterfacesState';
import { APIWordpress } from '../../redux/services/APIRequestsWordpress';

export type CaptchaMode = 'visible' | 'invisible';

export interface SmartCaptchaController {
  /** Капча сконфигурирована и должна показываться/проверяться. */
  enabled: boolean;
  sitekey: string;
  mode: CaptchaMode;
  /** Текущий токен (пустой, пока не пройдена). */
  token: string;
  /** key для пересоздания виджета при сбросе. */
  resetKey: number;
  /** Видимость невидимого виджета (триггер проверки). */
  visible: boolean;
  /** Текст ошибки капчи для показа в форме. */
  captchaError: string | null;

  // Колбэки для виджета (см. SmartCaptchaBox):
  onSuccess: (token: string) => void;
  onTokenExpired: () => void;
  onNetworkError: () => void;
  onChallengeHidden: () => void;

  /**
   * Гарантирует наличие токена перед отправкой формы.
   * - капча выключена → '' (форма не блокируется);
   * - visible → текущий токен либо reject с подсветкой ошибки;
   * - invisible → запускает проверку и резолвит токен в onSuccess.
   */
  ensureToken: () => Promise<string>;
  /** Сброс капчи (после успешной отправки, ошибки сервера, смены темы). */
  reset: () => void;
}

const CAPTCHA_ERROR_TEXT = 'Подтвердите, что вы не робот';
const CAPTCHA_RETRY_ERROR_TEXT = 'Не удалось пройти проверку, попробуйте отправить ещё раз';

/**
 * Сколько раз перевыпускаем токен в invisible-режиме, если предпроверка
 * на сервере его не подтвердила (Яндекс иногда выдаёт «битые» токены,
 * которые его же /validate не признаёт).
 */
const PRECHECK_ATTEMPTS = 5;

interface PendingResolver {
  resolve: (token: string) => void;
  reject: (reason?: any) => void;
}

/**
 * Управление состоянием Yandex SmartCaptcha. Конфиг (sitekey/mode/enabled)
 * берётся из ACF-опций, прокинутых в форму как contactForms.
 */
export const useSmartCaptcha = (contactForms?: IContactForms): SmartCaptchaController => {
  const sitekey = contactForms?.captcha_sitekey || '';
  const mode: CaptchaMode = contactForms?.captcha_mode === 'invisible' ? 'invisible' : 'visible';
  const enabled = !!contactForms?.captcha_enabled && !!sitekey;

  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [visible, setVisible] = useState(false);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const tokenRef = useRef('');
  const pendingRef = useRef<PendingResolver | null>(null);

  const setTokenSafe = useCallback((value: string) => {
    tokenRef.current = value;
    setToken(value);
  }, []);

  const reset = useCallback(() => {
    setTokenSafe('');
    setVisible(false);
    setCaptchaError(null);
    setResetKey((k) => k + 1);
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-reset'));
      pendingRef.current = null;
    }
  }, [setTokenSafe]);

  const onSuccess = useCallback((value: string) => {
    setTokenSafe(value);
    setCaptchaError(null);
    if (pendingRef.current) {
      pendingRef.current.resolve(value);
      pendingRef.current = null;
      setVisible(false);
    }
  }, [setTokenSafe]);

  const onTokenExpired = useCallback(() => {
    setTokenSafe('');
    setResetKey((k) => k + 1);
  }, [setTokenSafe]);

  const onNetworkError = useCallback(() => {
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-network-error'));
      pendingRef.current = null;
    }
    setVisible(false);
    setCaptchaError('Не удалось загрузить капчу, попробуйте ещё раз');
  }, []);

  const onChallengeHidden = useCallback(() => {
    // Пользователь закрыл челлендж, не решив капчу.
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-challenge-hidden'));
      pendingRef.current = null;
    }
    setVisible(false);
  }, []);

  const mintToken = useCallback((): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setVisible(true);
    });
  }, []);

  // Пересоздаём виджет, чтобы следующий execute() выдал новый токен.
  const discardToken = useCallback(() => {
    setTokenSafe('');
    setVisible(false);
    setResetKey((k) => k + 1);
  }, [setTokenSafe]);

  const ensureToken = useCallback(async (): Promise<string> => {
    if (!enabled) {
      return '';
    }

    if (mode === 'invisible') {
      for (let attempt = 1; attempt <= PRECHECK_ATTEMPTS; attempt++) {
        const token = tokenRef.current || await mintToken();
        if (await APIWordpress.precheckCaptcha(token)) {
          return token;
        }
        console.error(`SmartCaptcha: токен не прошёл предпроверку (попытка ${attempt} из ${PRECHECK_ATTEMPTS})`);
        discardToken();
      }
      setCaptchaError(CAPTCHA_RETRY_ERROR_TEXT);
      throw new Error('captcha-precheck-failed');
    }

    // visible: токен появляется после клика пользователя по чекбоксу.
    if (tokenRef.current) {
      const token = tokenRef.current;
      if (await APIWordpress.precheckCaptcha(token)) {
        return token;
      }
      discardToken();
      setCaptchaError(CAPTCHA_ERROR_TEXT);
      throw new Error('captcha-precheck-failed');
    }
    setCaptchaError(CAPTCHA_ERROR_TEXT);
    throw new Error('captcha-required');
  }, [enabled, mode, mintToken, discardToken]);

  return {
    enabled,
    sitekey,
    mode,
    token,
    resetKey,
    visible,
    captchaError,
    onSuccess,
    onTokenExpired,
    onNetworkError,
    onChallengeHidden,
    ensureToken,
    reset,
  };
};
